// AppSidebar — Sidebar principale de l'application (shadcn/ui Sidebar)
// Utilisée dans DashboardLayout pour le layout "app SaaS"

import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ClipboardList, ListChecks, Award, ShieldCheck, ChevronsUpDown, LogOut, Check } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { Badge } from '../ui/badge'
import { useEntreprise } from '../../contexts/EntrepriseContext'
import { useNotifications } from '../../contexts/NotificationsContext'
import { cn } from '../../lib/utils'
import { LogoPreventionScop } from './Header'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from '../ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

function getUserInitials(prenom?: string, nom?: string): string {
  const p = prenom?.charAt(0)?.toUpperCase() || ''
  const n = nom?.charAt(0)?.toUpperCase() || ''
  return p + n || '?'
}

export default function AppSidebar() {
  const { user, logout } = useAuth()
  const { organisations, currentEntreprise, setCurrentEntreprise } = useEntreprise()
  const location = useLocation()

  const { attestationsPending, planActionNew } = useNotifications()
  const userInitials = getUserInitials(user?.prenom, user?.nom)
  const fullName = user ? `${user.prenom} ${user.nom}` : 'Utilisateur'
  const email = user?.email || ''

  return (
    <Sidebar collapsible="icon">
      {/* Zone haute : Logos + Sélecteur entreprise */}
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-2">
          <span className="group-data-[collapsible=icon]:hidden">
            <LogoPreventionScop className="h-8 w-auto" />
          </span>
          <SidebarTrigger className="ml-auto" />
        </div>

        {/* Sélecteur d'entreprise */}
        {organisations.length > 0 && (
          <SidebarMenu>
            <SidebarMenuItem>
              {organisations.length === 1 ? (
                <SidebarMenuButton size="lg" className="cursor-default">
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground text-xs font-bold">
                    {getInitials(organisations[0])}
                  </div>
                  <div className="flex flex-col gap-0.5 leading-none">
                    <span className="font-semibold text-sm">{organisations[0]}</span>
                    <span className="text-xs text-muted-foreground">Entreprise</span>
                  </div>
                </SidebarMenuButton>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton size="lg">
                      <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground text-xs font-bold">
                        {currentEntreprise ? getInitials(currentEntreprise) : '?'}
                      </div>
                      <div className="flex flex-col gap-0.5 leading-none">
                        <span className="font-semibold text-sm">{currentEntreprise || 'Choisir'}</span>
                        <span className="text-xs text-muted-foreground">Entreprise</span>
                      </div>
                      <ChevronsUpDown className="ml-auto size-4" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="w-[--radix-dropdown-menu-trigger-width]"
                  >
                    {organisations.map((nom) => (
                      <DropdownMenuItem
                        key={nom}
                        onClick={() => setCurrentEntreprise(nom)}
                        className={cn(nom === currentEntreprise && 'bg-accent')}
                      >
                        <div className="flex items-center gap-2">
                          <div className="flex aspect-square size-6 items-center justify-center rounded bg-muted text-xs font-bold">
                            {getInitials(nom)}
                          </div>
                          <span>{nom}</span>
                        </div>
                        {nom === currentEntreprise && (
                          <Check className="ml-auto size-4" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarHeader>

      {/* Zone centrale : Navigation */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={location.pathname === '/dashboard'}
                tooltip="Diagnostics"
              >
                <Link to="/dashboard">
                  <ClipboardList className="size-4" />
                  <span>Diagnostics</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={location.pathname === '/dashboard/plan-action'}
                tooltip="Plan d'action"
              >
                <Link to="/dashboard/plan-action">
                  <ListChecks className="size-4" />
                  <span>Plan d'action</span>
                  {planActionNew > 0 && (
                    <Badge className="ml-auto text-xs px-1.5 py-0 bg-primary text-primary-foreground">
                      {planActionNew}
                    </Badge>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={location.pathname === '/dashboard/attestation'}
                tooltip="Attestation"
              >
                <Link to="/dashboard/attestation">
                  <Award className="size-4" />
                  <span>Attestation</span>
                  {attestationsPending > 0 && (
                    <Badge className="ml-auto text-xs px-1.5 py-0 bg-amber-500 text-white">
                      {attestationsPending}
                    </Badge>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        {/* Section Administration attestations — visible uniquement pour les admins */}
        {user?.roles?.includes('ADMIN_ATTESTATION') && (
          <AdminSection pathname={location.pathname} />
        )}

      </SidebarContent>

      {/* Zone basse : Utilisateur connecté */}
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg">
                  <div className="flex aspect-square size-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <span className="text-xs font-medium">{userInitials}</span>
                  </div>
                  <div className="flex flex-col gap-0.5 leading-none">
                    <span className="font-semibold text-sm">{fullName}</span>
                    <span className="text-xs text-muted-foreground">{email}</span>
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                side="top"
                className="w-[--radix-dropdown-menu-trigger-width]"
              >
                <DropdownMenuItem onClick={() => logout()}>
                  <LogOut className="size-4 mr-2" />
                  Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}

// Section admin avec compteur de demandes en attente
function AdminSection({ pathname }: { pathname: string }) {
  const [pendingCount, setPendingCount] = useState(0)

  useEffect(() => {
    async function fetchPendingCount() {
      try {
        const res = await fetch('/api/admin/attestations?statut=en_attente&_start=0&_end=0', {
          credentials: 'include',
        })
        if (res.ok) {
          const total = parseInt(res.headers.get('x-total-count') || '0', 10)
          setPendingCount(total)
        }
      } catch {
        // Silently fail — not critical
      }
    }
    fetchPendingCount()
  }, [])

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Administration</SidebarGroupLabel>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            isActive={pathname.startsWith('/admin/attestations')}
            tooltip="Validation attestations"
          >
            <Link to="/admin/attestations">
              <ShieldCheck className="size-4" />
              <span>Attestations</span>
              {pendingCount > 0 && (
                <Badge variant="destructive" className="ml-auto text-xs px-1.5 py-0">
                  {pendingCount}
                </Badge>
              )}
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  )
}

